// @ts-nocheck
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface DemoSession {
  id: string;
  email: string;
  prospect_name: string | null;
  company: string | null;
}

interface DemoContextType {
  isDemoMode: boolean;
  demoSession: DemoSession | null;
  isLoading: boolean;
  validateToken: (token: string) => Promise<boolean>;
  requestAccess: (email: string, name?: string, company?: string) => Promise<{ success: boolean; magicLink?: string; error?: string }>;
  exitDemo: () => void;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

export const useDemoContext = () => {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemoContext must be used within DemoProvider');
  return ctx;
};

const DEMO_SESSION_KEY = 'recruitlook-demo-session';

export const DemoProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [demoSession, setDemoSession] = useState<DemoSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Restore session from localStorage
    const stored = localStorage.getItem(DEMO_SESSION_KEY);
    if (stored) {
      try {
        setDemoSession(JSON.parse(stored));
      } catch {}
    }
    setIsLoading(false);
  }, []);

  const validateToken = async (token: string): Promise<boolean> => {
    try {
      const { data, error } = await invokeEdgeFunction('demo-validate-token', {
        body: { token },
      });
      if (error || !data?.success) return false;
      const session = data.session as DemoSession;
      setDemoSession(session);
      localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(session));
      return true;
    } catch {
      return false;
    }
  };

  const requestAccess = async (email: string, name?: string, company?: string) => {
    try {
      const { data, error } = await invokeEdgeFunction('demo-magic-link', {
        body: { email, name, company },
      });
      if (error) return { success: false, error: 'Failed to request access' };
      return { success: true, magicLink: data?.magicLink };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const exitDemo = () => {
    setDemoSession(null);
    localStorage.removeItem(DEMO_SESSION_KEY);
  };

  return (
    <DemoContext.Provider
      value={{
        isDemoMode: !!demoSession,
        demoSession,
        isLoading,
        validateToken,
        requestAccess,
        exitDemo,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
};